import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LeadSection } from "@/components/LeadSection";
import { StickyCta } from "@/components/StickyCta";
import { itemListSchema, SITE_URL } from "@/lib/seo";

const TITLE = "Shopify Fragen: kurze, klare Antworten";
const DESCRIPTION =
  "Direkte Antworten auf die häufigsten Shopify-Fragen: Kosten, Tarife, Dauer, Agentur oder Freelancer und wann sich Shopify Plus lohnt.";

const questions = [
  {
    to: "/fragen/was-kostet-ein-shopify-shop" as const,
    q: "Was kostet ein Shopify-Shop?",
    a: "Laufende Kosten aus Tarif, Zahlungsgebühren und Apps, dazu die einmalige Umsetzung.",
  },
  {
    to: "/fragen/welcher-shopify-tarif" as const,
    q: "Welcher Shopify-Tarif ist der richtige?",
    a: "Wie Sie den Tarif am Umsatz, an den Zahlungsgebühren und am Funktionsbedarf festmachen.",
  },
  {
    to: "/fragen/wie-lange-dauert-ein-shopify-shop" as const,
    q: "Wie lange dauert ein Shopify-Shop?",
    a: "Realistische Zeitrahmen nach Umfang und was den Start am häufigsten verzögert.",
  },
  {
    to: "/fragen/shopify-agentur-oder-freelancer" as const,
    q: "Shopify Agentur oder Freelancer?",
    a: "Wann ein Team sinnvoll ist und wann eine Person der kürzere Weg ist.",
  },
  {
    to: "/fragen/wann-lohnt-sich-shopify-plus" as const,
    q: "Wann lohnt sich Shopify Plus?",
    a: "Die konkreten Grenzen des Standardtarifs, an denen ein Wechsel sinnvoll wird.",
  },
  {
    to: "/fragen/was-kostet-ein-shopify-freelancer" as const,
    q: "Was kostet ein Shopify Freelancer?",
    a: "Stundensatz, Festpreise und was den Preis nach oben treibt.",
  },
  {
    to: "/fragen/shopify-oder-woocommerce" as const,
    q: "Shopify oder WooCommerce?",
    a: "Der ehrliche Vergleich: Kosten, Pflegeaufwand und für wen sich was lohnt.",
  },
  {
    to: "/fragen/was-kostet-eine-shopify-theme-anpassung" as const,
    q: "Was kostet eine Theme-Anpassung?",
    a: "Typische Aufgaben, Stundensatz und wann sich ein Festpreis lohnt.",
  },
  {
    to: "/fragen/warum-kauft-niemand-in-meinem-shop" as const,
    q: "Warum kauft niemand in meinem Shop?",
    a: "Die drei häufigsten Ursachen und wie Sie die richtige finden.",
  },
  {
    to: "/fragen/wie-lange-dauert-shopify-seo" as const,
    q: "Wie lange dauert Shopify SEO?",
    a: "Was in Wochen wirkt, was Monate braucht und wie Sie überbrücken.",
  },
  {
    to: "/fragen/welche-shopify-apps-brauche-ich" as const,
    q: "Welche Shopify Apps brauche ich?",
    a: "Was sich wirklich lohnt und warum zu viele Apps den Shop ausbremsen.",
  },
  {
    to: "/fragen/shopify-shop-betreuen-lassen" as const,
    q: "Shop betreuen lassen: was kostet das?",
    a: "Laufende Betreuung ohne Mindestlaufzeit, abgerechnet nach Stunden.",
  },
  {
    to: "/fragen/shopify-gebuehren" as const,
    q: "Welche Gebühren fallen bei Shopify an?",
    a: "Tarif, Zahlungsgebühren, Apps und Theme verständlich erklärt.",
  },
  {
    to: "/fragen/shopify-b2b" as const,
    q: "Eignet sich Shopify für B2B?",
    a: "Händlerpreise, Kauf auf Rechnung und wann Shopify Plus nötig ist.",
  },
  {
    to: "/fragen/shopify-erfahrungen" as const,
    q: "Shopify Erfahrungen: Vorteile und Nachteile",
    a: "Vorteile, Nachteile und für wen sich Shopify lohnt.",
  },
  {
    to: "/fragen/shopify-vs-shopware" as const,
    q: "Shopify oder Shopware: was passt besser?",
    a: "Kosten, Pflegeaufwand und für wen sich welches System lohnt.",
  },
  {
    to: "/fragen/shopify-zahlungsarten" as const,
    q: "Welche Zahlungsarten brauche ich in meinem Shopify-Shop?",
    a: "Was Kunden in DE, AT und CH erwarten und wie Abbrüche sinken.",
  },
];

export const Route = createFileRoute("/fragen/")({
  component: FragenPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE_URL}/fragen` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/fragen` }],
    scripts: [
      itemListSchema(
        questions.map((q) => ({ name: q.q, path: q.to })),
        "Häufige Shopify-Fragen",
      ),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: questions.map((q) => ({
            "@type": "Question",
            name: q.q,
            acceptedAnswer: { "@type": "Answer", text: q.a },
          })),
        }),
      },
    ],
  }),
});

function FragenPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <section className="veil border-b border-border/60 py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h1 className="max-w-3xl text-4xl font-bold leading-[1.08] md:text-5xl">
              Shopify Fragen, <span className="text-signal">kurz beantwortet</span>
            </h1>
            <p className="mt-5 max-w-xl text-muted-foreground">
              Jede Seite beginnt mit der Antwort in zwei bis drei Sätzen. Details stehen darunter,
              falls Sie tiefer einsteigen wollen.
            </p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground">
              So kommen Sie ohne Umwege von Ihrer Frage zu einer belastbaren Entscheidung für
              Ihren Shop.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto grid max-w-6xl gap-5 px-5 md:grid-cols-2">
            {questions.map((q) => (
              <Link
                key={q.to}
                to={q.to}
                className="shadow-lift group rounded-lg border border-border bg-card p-6 transition-transform hover:-translate-y-0.5"
              >
                <h2 className="text-lg font-semibold md:text-xl">{q.q}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{q.a}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                  Antwort lesen <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </section>

        <LeadSection source="fragen-hub" />
      </main>
      <StickyCta />
      <SiteFooter />
    </div>
  );
}
